/**
 * Temporal anti-aliasing.
 *
 *  - Halton(2,3) sub-pixel jitter, 8 phases
 *  - velocity-buffer reprojection with 3x3 closest-depth dilation
 *  - Catmull-Rom history filtering (keeps the resolve from going soft)
 *  - YCoCg variance clipping + a variance-scaled history rejection term
 *  - luminance-weighted blend (Karis) to kill highlight flicker
 *  - RCAS resolve so the result does not read as smeared
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const TAA_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}

uniform sampler2D tCurrent;
uniform sampler2D tHistory;
uniform sampler2D tData;      // rg velocity, b viewZ, a flags
uniform vec2 uTexel;
uniform float uAlpha;
uniform float uReset;
uniform float uClampGamma;

varying vec2 vUv;

vec4 catmullRom(sampler2D tex, vec2 uv, vec2 texel) {
  vec2 texSize = 1.0 / texel;
  vec2 samplePos = uv * texSize;
  vec2 texPos1 = floor(samplePos - 0.5) + 0.5;
  vec2 f = samplePos - texPos1;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);
  vec2 w12 = w1 + w2;
  vec2 off12 = w2 / max(w12, vec2(1e-5));
  vec2 p0 = (texPos1 - 1.0) * texel;
  vec2 p3 = (texPos1 + 2.0) * texel;
  vec2 p12 = (texPos1 + off12) * texel;
  vec4 r = vec4(0.0);
  r += texture2D(tex, vec2(p0.x, p12.y)) * (w0.x * w12.y);
  r += texture2D(tex, vec2(p12.x, p0.y)) * (w12.x * w0.y);
  r += texture2D(tex, vec2(p12.x, p12.y)) * (w12.x * w12.y);
  r += texture2D(tex, vec2(p3.x, p12.y)) * (w3.x * w12.y);
  r += texture2D(tex, vec2(p12.x, p3.y)) * (w12.x * w3.y);
  return r;
}

vec3 clipToAABB(vec3 c, vec3 mn, vec3 mx, vec3 mu) {
  vec3 center = 0.5 * (mx + mn);
  vec3 extent = 0.5 * (mx - mn) + 1e-5;
  vec3 dir = c - center;
  vec3 ts = abs(extent / max(abs(dir), vec3(1e-5)));
  float t = saturate(min(ts.x, min(ts.y, ts.z)));
  return center + dir * t;
}

void main() {
  vec3 cur = pfSanitize(texture2D(tCurrent, vUv).rgb);

  // --- dilate velocity: take the closest fragment in a 3x3 neighbourhood
  vec2 vel = vec2(0.0);
  float bestZ = 1e9;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec4 dta = texture2D(tData, vUv + vec2(float(x), float(y)) * uTexel);
      float z = dta.b > 0.0 ? dta.b : 1e8;
      if (z < bestZ) { bestZ = z; vel = dta.rg; }
    }
  }

  vec2 histUv = vUv - vel;

  // --- neighbourhood statistics in YCoCg
  vec3 m1 = vec3(0.0), m2 = vec3(0.0);
  vec3 nmin = vec3(1e9), nmax = vec3(-1e9);
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec3 s = rgb2ycocg(pfSanitize(texture2D(tCurrent, vUv + vec2(float(x), float(y)) * uTexel).rgb));
      m1 += s; m2 += s * s;
      nmin = min(nmin, s); nmax = max(nmax, s);
    }
  }
  vec3 mu = m1 / 9.0;
  vec3 sigma = sqrt(max(vec3(0.0), m2 / 9.0 - mu * mu));
  /* The variance box is intersected with the true neighbourhood min/max, so a
     small gamma makes the clamp *tighter* than the real signal. On a 2 px cable
     against sky the 3x3 window is eight sky samples and one cable sample: the
     variance box sits well above the cable, the correctly-resolved dark history
     is clipped up toward the sky every frame, and the cable erodes away. Keep
     the box wide enough to contain a legitimate one-in-nine outlier; ghosting is
     held off by the velocity/rejection terms on the blend weight below, not by
     strangling thin geometry. */
  vec3 mn = max(mu - uClampGamma * sigma, nmin);
  vec3 mx = min(mu + uClampGamma * sigma, nmax);

  bool valid = histUv.x > 0.0 && histUv.x < 1.0 && histUv.y > 0.0 && histUv.y < 1.0 && uReset < 0.5;
  if (!valid) {
    gl_FragColor = vec4(cur, 1.0);
    return;
  }

  vec3 hist = pfSanitize(catmullRom(tHistory, histUv, uTexel).rgb);
  vec3 histY = rgb2ycocg(hist);
  vec3 clipped = clipToAABB(histY, mn, mx, mu);

  // how far the history had to move tells us how stale it is
  float rejection = length(clipped - histY) / (length(sigma) + 1e-3);
  hist = max(ycocg2rgb(clipped), vec3(0.0));

  float alpha = uAlpha;
  alpha = mix(alpha, 0.35, saturate(rejection * 0.35));
  alpha = mix(alpha, 0.45, saturate(length(vel / uTexel) * 0.02));

  // luminance weighted blend keeps bright specks from flickering
  float wc = alpha / (1.0 + pfLuma(cur));
  float wh = (1.0 - alpha) / (1.0 + pfLuma(hist));
  vec3 outc = (cur * wc + hist * wh) / max(wc + wh, 1e-5);

  gl_FragColor = vec4(outc, 1.0);
}
`;

const RCAS_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tColor;
uniform vec2 uTexel;
uniform float uSharpness;
varying vec2 vUv;

vec3 tm(vec3 c) { return c / (1.0 + maxc(c)); }
vec3 itm(vec3 c) { return c / max(1e-4, 1.0 - maxc(c)); }

void main() {
  vec3 e = tm(max(texture2D(tColor, vUv).rgb, vec3(0.0)));
  vec3 b = tm(max(texture2D(tColor, vUv + vec2(0.0, -uTexel.y)).rgb, vec3(0.0)));
  vec3 d = tm(max(texture2D(tColor, vUv + vec2(-uTexel.x, 0.0)).rgb, vec3(0.0)));
  vec3 f = tm(max(texture2D(tColor, vUv + vec2(uTexel.x, 0.0)).rgb, vec3(0.0)));
  vec3 h = tm(max(texture2D(tColor, vUv + vec2(0.0, uTexel.y)).rgb, vec3(0.0)));

  vec3 mn = min(min(min(b, d), min(f, h)), e);
  vec3 mx = max(max(max(b, d), max(f, h)), e);

  vec3 hitMin = mn / max(4.0 * mx, vec3(1e-4));
  vec3 hitMax = (1.0 - mx) / max(4.0 * mn - 4.0, vec3(-1e4));
  vec3 lobe3 = max(-hitMin, hitMax);
  float lobe = max(-0.1875, min(maxc(lobe3), 0.0)) * uSharpness;

  vec3 res = (lobe * (b + d + f + h) + e) / (4.0 * lobe + 1.0);
  gl_FragColor = vec4(itm(max(res, vec3(0.0))), 1.0);
}
`;

export class TAA {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.histA = null; this.histB = null; this.sharpRT = null;
    this.w = 0; this.h = 0;
    this.ping = 0;
    this.reset = true;

    this.mat = makeMaterial({
      name: 'postfx.taa',
      fragmentShader: TAA_FRAG,
      uniforms: {
        tCurrent: { value: null }, tHistory: { value: null }, tData: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uAlpha: { value: 0.09 },
        uReset: { value: 1 },
        uClampGamma: { value: 2.6 },
      },
    });

    this.matSharp = makeMaterial({
      name: 'postfx.rcas',
      fragmentShader: RCAS_FRAG,
      uniforms: {
        tColor: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uSharpness: { value: 0.75 },
      },
    });
  }

  setSize(w, h) {
    if (this.w === w && this.h === h) return;
    this.w = w; this.h = h;
    for (const rt of [this.histA, this.histB, this.sharpRT]) if (rt) rt.dispose();
    this.histA = makeRT(w, h, { name: 'postfx.taa.a' });
    this.histB = makeRT(w, h, { name: 'postfx.taa.b' });
    this.sharpRT = makeRT(w, h, { name: 'postfx.taa.sharp' });
    this.reset = true;
  }

  resetHistory() { this.reset = true; }

  /** @returns {THREE.Texture} the sharpened resolve */
  render(renderer, { colorTexture, dataTexture, alpha, sharpness }) {
    const src = this.ping ? this.histB : this.histA;
    const dst = this.ping ? this.histA : this.histB;
    this.ping ^= 1;

    const u = this.mat.uniforms;
    u.tCurrent.value = colorTexture;
    u.tHistory.value = src.texture;
    u.tData.value = dataTexture;
    u.uTexel.value.set(1 / this.w, 1 / this.h);
    u.uAlpha.value = alpha;
    u.uReset.value = this.reset ? 1 : 0;
    this.blit.draw(renderer, this.mat, dst);
    this.reset = false;

    const s = this.matSharp.uniforms;
    s.tColor.value = dst.texture;
    s.uTexel.value.set(1 / this.w, 1 / this.h);
    s.uSharpness.value = sharpness;
    this.blit.draw(renderer, this.matSharp, this.sharpRT);

    return this.sharpRT.texture;
  }

  dispose() {
    for (const rt of [this.histA, this.histB, this.sharpRT]) if (rt) rt.dispose();
    this.mat.dispose(); this.matSharp.dispose();
  }
}
